import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import swal from 'sweetalert2';
import {
  LOCAL_DEFAULT_PAGE,
  ENV,
  LOCAL_API_URL,
  LOCAL_API_URL_V2,
  LOCAL_ECHO_CONFIG_AUTH_ENDPOINT,
  STAGING_API_URL,
  STAGING_API_URL_V2,
  STAGING_ECHO_CONFIG_AUTH_ENDPOINT,
  PROD_BASE_URL,
  PROD_API_BASE_URL,
  LOCAL_BASE_URL,
  STAGING_BASE_URL,
  STAGING_DEFAULT_PAGE,
  PROD_DEFAULT_PAGE,
  BASIC_TENANT,
  STAGING_SPLIT_AUTHORIZATION_KEY,
  PROD_SPLIT_AUTHORIZATION_KEY,
  STAGING_TENANT
} from './environment.constant';
import { lastValueFrom, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  private initialization: Promise<void> | null = null;
  constructor(private http: HttpClient) {}

  initializeTenantEnvironment(): Promise<void> {
    if (!this.initialization) {
      this.initialization = this.initialize().catch(error => {
        this.initialization = null;
        throw error;
      });
    }
    return this.initialization;
  }

  private lookupTenant(url: string) {
    // Only this read-only bootstrap lookup retries; never retry a business mutation.
    return this.http.get(url).pipe(retry({ count: 2, delay: (error, attempt) =>
      error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500
        ? timer(250 * attempt) : throwError(() => error)
    }));
  }

  /**
   * Initialize tenant-specific environment variables based on the subdomain and environment.
   */
  private async initialize(): Promise<void> {
    const domain = window.location.hostname;
    const currentEnv = this.determineEnvironment(domain);

    if (currentEnv === ENV.LOCAL) {
      await this.handleLocalEnvironment(domain);
    } else if (currentEnv === ENV.STAGING) {
      await this.handleStagingEnvironment(domain);
    } else if (currentEnv === ENV.PRODUCTION) {
      await this.handleProductionEnvironment(domain);
    } else {
      this.setDefaultEnvironment(ENV.LOCAL);
    }
  }

  private determineEnvironment(domain: string): string {
    // Check for local environment
    if (domain.includes('localhost') || domain.includes('127.0.0.1')) {
      return ENV.LOCAL;
    }

    // Check for staging environment
    if (domain.includes('staging.')) {
      return ENV.STAGING;
    }

    // Check for production environment
    // This will match any domain that ends with mymedsph.app
    // This includes wildcard domains like *.mymedsph.app
    const domainParts = domain.split('.');
    if (domainParts.length >= 2) {
      const lastTwoParts = domainParts.slice(-2).join('.');
      if (lastTwoParts === PROD_BASE_URL) {
        return ENV.PRODUCTION;
      }
    }

    return ENV.LOCAL;
  }

  private async handleLocalEnvironment(domain: string): Promise<void> {
    const defaultDomain = new URL(LOCAL_DEFAULT_PAGE).hostname;
    if (this.isSubDomain(domain, defaultDomain)) {
      const subDomain = this.extractSubDomain(domain, defaultDomain);
      try {
        const data: any = await lastValueFrom(this.lookupTenant(`${LOCAL_BASE_URL}/api/tenant/${subDomain}`));
        this.configureEnvironment(subDomain, ENV.LOCAL);
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    } else {
      this.setDefaultEnvironment(ENV.LOCAL);
    }
  }

  private async handleStagingEnvironment(domain: string): Promise<void> {
    const subDomain = this.extractTenantFromDomain(domain);
    if (subDomain) {
      try {
        const data: any = await lastValueFrom(this.lookupTenant(`${STAGING_BASE_URL}/api/tenant/${subDomain}`));
        this.configureEnvironment(subDomain, ENV.STAGING);
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    } else {
      this.setDefaultEnvironment(ENV.STAGING);
    }
  }

  private async handleProductionEnvironment(domain: string): Promise<void> {
    const subDomain = this.extractTenantFromDomain(domain);
    if (subDomain) {
      try {
        const basicTenantId = STAGING_TENANT.has(subDomain) ? 'staging' : BASIC_TENANT;
        const data: any = await lastValueFrom(this.lookupTenant(`https://${basicTenantId}.${PROD_API_BASE_URL}/api/tenant/${subDomain}`));
        this.configureEnvironment(subDomain, ENV.PRODUCTION);
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    } else {
      this.setDefaultEnvironment(ENV.PRODUCTION);
    }
  }

  private isSubDomain(domain: string, defaultDomain: string): boolean {
    const domainParts = domain.split('.');
    const defaultDomainParts = defaultDomain.split('.');
    return domainParts.length > defaultDomainParts.length;
  }

  private extractSubDomain(domain: string, defaultDomain: string): string {
    if (domain.endsWith(defaultDomain)) {
      return domain.replace(`.${defaultDomain}`, '');
    }
    return '';
  }

  private extractTenantFromDomain(domain: string): string {
    const parts = domain.split('.');
    // For production, we want the first part of the domain
    // This will work for both regular and wildcard domains
    if (parts.length >= 2) {
      return parts[0];
    }
    return '';
  }

  private configureEnvironment(subDomain: string, env: string): void {
    switch (env) {
      case ENV.LOCAL:
        const localApiDomain = `${subDomain}.localhost:8013`;
        environment.baseApiUrl = `${LOCAL_BASE_URL}/api`;
        environment.apiUrl = `http://${localApiDomain}/api/dental`;
        environment.apiUrlV2 = `http://${localApiDomain}/api`;
        environment.ECHO_CONFIG.authEndpoint = `http://${localApiDomain}/api/broadcasting/auth`;
        break;
      case ENV.STAGING:
        const stagingApiDomain = `staging.api.mymedsph.app`;
        environment.baseApiUrl = `${STAGING_BASE_URL}/api`;
        environment.apiUrl = STAGING_API_URL;
        environment.apiUrlV2 = STAGING_API_URL_V2;
        environment.ECHO_CONFIG.authEndpoint = STAGING_ECHO_CONFIG_AUTH_ENDPOINT;
        environment.splitAuthorizationKey = STAGING_SPLIT_AUTHORIZATION_KEY;
        break;
      case ENV.PRODUCTION:
        const apiDomain = `${subDomain}.${PROD_API_BASE_URL}`;
        environment.baseApiUrl = `https://${PROD_API_BASE_URL}/api`;
        environment.apiUrl = `https://${apiDomain}/api/dental`;
        environment.apiUrlV2 = `https://${apiDomain}/api`;
        environment.ECHO_CONFIG.authEndpoint = `https://${apiDomain}/api/broadcasting/auth`;
        environment.splitAuthorizationKey = PROD_SPLIT_AUTHORIZATION_KEY;
        break;
    }
  }

  private handleError(err: any): void {
    const domain = window.location.hostname;
    let defaultPage: any;
    if (domain.includes('localhost') || domain.includes('127.0.0.1')) {
      defaultPage = LOCAL_DEFAULT_PAGE;
    } else if (domain.includes('staging.') || STAGING_TENANT.has(domain.split('.')[0])) {
      defaultPage = STAGING_DEFAULT_PAGE;
    } else {
      defaultPage = PROD_DEFAULT_PAGE;
    }
    if (err?.error?.error === 'Domain not found') {
      window.location.href = `${defaultPage}/domain-not-found?domain=${domain}`;
    }
  }

  private setDefaultEnvironment(env: string): void {
    switch (env) {
      case ENV.LOCAL:
        environment.baseApiUrl = `${LOCAL_BASE_URL}/api`;
        environment.apiUrl = LOCAL_API_URL;
        environment.apiUrlV2 = LOCAL_API_URL_V2;
        environment.ECHO_CONFIG.authEndpoint = LOCAL_ECHO_CONFIG_AUTH_ENDPOINT;
        break;
      case ENV.STAGING:
        environment.baseApiUrl = `${STAGING_BASE_URL}/api`;
        environment.apiUrl = STAGING_API_URL;
        environment.apiUrlV2 = STAGING_API_URL_V2;
        environment.ECHO_CONFIG.authEndpoint = STAGING_ECHO_CONFIG_AUTH_ENDPOINT;
        environment.splitAuthorizationKey = STAGING_SPLIT_AUTHORIZATION_KEY;
        break;
      case ENV.PRODUCTION:
        environment.baseApiUrl = `https://${BASIC_TENANT}.${PROD_API_BASE_URL}/api`;
        environment.apiUrl = `https://${BASIC_TENANT}.${PROD_API_BASE_URL}/api/dental`;
        environment.apiUrlV2 = `https://${BASIC_TENANT}.${PROD_API_BASE_URL}/api`;
        environment.ECHO_CONFIG.authEndpoint = `https://${BASIC_TENANT}.${PROD_API_BASE_URL}/api/broadcasting/auth`;
        environment.splitAuthorizationKey = PROD_SPLIT_AUTHORIZATION_KEY;
        break;
    }
  }
}
